import { Context } from 'lib/api'
import React from 'react'

export function getOrganizationSchema(ctx: Context) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: ctx.theme.title,
    url: ctx.theme.canonicalURL,
    logo: `${ctx.theme.canonicalURL}${ctx.theme.logoDark}`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: ctx.theme.contactInformation.phone,
      contactType: 'customer service',
      email: ctx.theme.contactInformation.mail,
    },
    sameAs: [
      ctx.theme.contactInformation.telegram
        ? `https://t.me/${ctx.theme.contactInformation.telegram}`
        : null,
      ctx.theme.contactInformation.instagram
        ? `https://instagram.com/${ctx.theme.contactInformation.instagram}`
        : null,
      ctx.theme.contactInformation.youtube
        ? `https://youtube.com/${ctx.theme.contactInformation.youtube}`
        : null,
    ].filter(Boolean),
  }
}

export type BreadcrumbItem = {
  name: string
  url: string
}

export function getBreadcrumbSchema(ctx: Context, items: BreadcrumbItem[]) {
  const allItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Главная',
      item: ctx.theme.canonicalURL,
    },
    ...items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 2,
      name: item.name,
      item: item.url,
    })),
  ]

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: allItems,
  }
}

export type ReviewItem = {
  heading: string
  text: string
  author: {
    name: string
    role: string
  }
}

export function getReviewSchema(ctx: Context, reviews: ReviewItem[]) {
  return reviews.map((review) => ({
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'SoftwareApplication',
      name: ctx.theme.title,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '5',
      bestRating: '5',
    },
    reviewBody: `${review.heading} ${review.text}`,
    author: {
      '@type': 'Person',
      name: review.author.name,
      jobTitle: review.author.role,
    },
  }))
}

export type FAQItem = {
  question: string
  answer: string
}

function extractTextFromJSX(element: React.ReactNode): string {
  if (typeof element === 'string' || typeof element === 'number') {
    return String(element)
  }
  if (Array.isArray(element)) {
    return element.map(extractTextFromJSX).join(' ')
  }
  if (React.isValidElement(element)) {
    if (element.props.children) {
      return extractTextFromJSX(element.props.children)
    }
  }
  return ''
}

export function getFAQPageSchema(faqs: { question: string; answer: React.ReactNode }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: extractTextFromJSX(faq.answer),
      },
    })),
  }
}

export type ItemListItem = {
  name: string
  url: string
}

export function getItemListSchema(ctx: Context, items: ItemListItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}
